import type {Context} from '../types';

/**
 * Existing format for sort data in the DynamoWrapper node.
 * data.dyn.query = {
 *  ..., // other fields
 *  sort?: Array<string>
 * }
 *
 * If sort exist we need to migrate to the new format which is
 * data.dyn.query = {
 *  ..., // other fields
 *  sort: Array<{field: string, direction: 'asc' | 'desc'}>
 * }
 *
 * This is the json node data of this WFDL Type structure
 *  TList(
 *    TRecord({
 *      field: TText,
 *      direction: TEnum({
 *        asc: 'Ascending',
 *        desc: 'Descending',
 *      })
 *    })
 *  )
 *
 * If no sort data is found we can keep not adding it in
 */

export default function (context: Context) {
  const {jsonNode} = context.node;

  const isDynamoWrapper =
    jsonNode.type === 'DynamoWrapper' || jsonNode.data?.dyn?.type === 'wrapper';

  const dataDynQuery = jsonNode.data?.dyn?.query;
  const dataDynQuerySort = dataDynQuery?.sort;

  const hasQuerySortStrings =
    Array.isArray(dataDynQuerySort) &&
    dataDynQuerySort.some((sort) => typeof sort === 'string');

  const needed = isDynamoWrapper && hasQuerySortStrings;

  return {
    needed,
    logData: {
      msg: 'DynamoWrapper migrating sort data to new format',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      // Need to recheck this to refine the data
      const sortData = jsonNode.data?.dyn?.query?.sort;

      const dataDynQuerySortIsArray = Array.isArray(sortData);

      if (!dataDynQuerySortIsArray || sortData.length === 0) {
        return;
      }

      const newSortData = sortData.reduce((acc, currentSort) => {
        // If sort data is not a string we don't need to do anything, keep as is
        if (typeof currentSort !== 'string') {
          acc.push(currentSort);
        } else {
          const descending = currentSort.startsWith('-');
          const field = descending ? currentSort.slice(1) : currentSort;
          const newField = {
            field,
            direction: descending ? 'desc' : 'asc',
          };
          acc.push(newField);
        }
        return acc;
      }, []);

      // set to the json node
      if (!jsonNode.data) {
        jsonNode.data = {};
      }
      if (!jsonNode.data.dyn) {
        jsonNode.data.dyn = {};
      }
      if (!jsonNode.data.dyn.query) {
        jsonNode.data.dyn.query = {};
      }

      jsonNode.data.dyn.query.sort = newSortData;
    },
  };
}
